import { GLOBAL_ELIGIBLE_ROLES, GLOBAL_SCOPE_ROLES, ROLES, STAFF_ROLES } from "./roles";

// Roles for which "every restaurant" is a meaningful scope at all (the Users screen offers the switch only to these).
export const isGlobalEligibleRole = (role) => GLOBAL_ELIGIBLE_ROLES.includes(role);

// An eligible staff role holding an explicit `globalAccess: true` (an unset flag is never global).
export const hasGlobalStaffAccess = (user) => user?.globalAccess === true && GLOBAL_ELIGIBLE_ROLES.includes(user?.role);

// Every restaurant: the ceo-level tier, and an eligible staff role with an explicit `globalAccess: true`.
export const reachesAllRestaurants = (role, user) =>
  GLOBAL_SCOPE_ROLES.includes(role) || (user?.globalAccess === true && GLOBAL_ELIGIBLE_ROLES.includes(role));

export const SCOPES = Object.freeze({
  RESTAURANT: "restaurant",
  BRANCH: "branch",
});

export const SCOPE_REASONS = Object.freeze({
  OK: "OK",
  MISSING_RESOURCE: "MISSING_RESOURCE",
  RESOURCE_NOT_ASSIGNED: "RESOURCE_NOT_ASSIGNED",
});

// Accept only plain string / number ids. Objects, NaN, booleans and empty
// strings are treated as "no id" so they can never match an assignment.
export const normalizeId = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
};

export const normalizeIdList = (list) =>
  Array.isArray(list) ? list.map(normalizeId).filter(Boolean) : [];

const result = (ok, reason) => ({ ok, reason });

const checkRestaurant = (role, between, restaurantId, user) => {
  if (reachesAllRestaurants(role, user)) return result(true, SCOPE_REASONS.OK);
  if (!restaurantId) return result(false, SCOPE_REASONS.MISSING_RESOURCE);
  if (between.length > 0) {
    return between.includes(restaurantId)
      ? result(true, SCOPE_REASONS.OK)
      : result(false, SCOPE_REASONS.RESOURCE_NOT_ASSIGNED);
  }
  // No assignment list: staff are filtered by the backend, clients have none.
  return STAFF_ROLES.includes(role)
    ? result(true, SCOPE_REASONS.OK)
    : result(false, SCOPE_REASONS.RESOURCE_NOT_ASSIGNED);
};

// scope: "restaurant" | "branch". `resource` is { restaurantId?, branchId? }.
// A missing id never means "every resource": scoped checks deny without it.
export const checkScope = (scope, { role, user }, resource) => {
  const restaurantId = normalizeId(resource?.restaurantId);
  const branchId = normalizeId(resource?.branchId);
  const between = normalizeIdList(user?.between);
  const assignedBranch = normalizeIdList(user?.assignedBranch);

  if (scope === SCOPES.RESTAURANT) {
    return checkRestaurant(role, between, restaurantId, user);
  }

  if (scope === SCOPES.BRANCH) {
    if (GLOBAL_SCOPE_ROLES.includes(role)) return result(true, SCOPE_REASONS.OK);
    if (!branchId) return result(false, SCOPE_REASONS.MISSING_RESOURCE);
    if (assignedBranch.length > 0) {
      if (!assignedBranch.includes(branchId)) {
        return result(false, SCOPE_REASONS.RESOURCE_NOT_ASSIGNED);
      }
      // Optional cross-check when the caller also knows the restaurant.
      if (restaurantId && between.length > 0 && !between.includes(restaurantId)) {
        return result(false, SCOPE_REASONS.RESOURCE_NOT_ASSIGNED);
      }
      return result(true, SCOPE_REASONS.OK);
    }
    // A branch user reaches only the branches assigned to them; with no valid
    // assignment they reach none (NA-020). Their role is never upgraded.
    if (role === ROLES.BRANCH) {
      return result(false, SCOPE_REASONS.RESOURCE_NOT_ASSIGNED);
    }
    // Any other role has no branch list: a branch is reachable through its
    // restaurant.
    return checkRestaurant(role, between, restaurantId, user);
  }

  return result(false, SCOPE_REASONS.RESOURCE_NOT_ASSIGNED);
};
